import { api_configuration } from "../../common";
import api_cs = api_configuration.types.client_state.alan_api;
import api_app = api_configuration.libraries.gui_definition_lib.libraries.model_lib.types.application.alan_api;
import { DatastoreProxy } from "../interface/DatastoreProxy";
import { createIdPathBuilderFromNode } from "../navigation/createIdPathFromNode";
import { CollectionPathBuilder } from "../navigation/createCollectionPathBuilder";
import * as Entry from "./Entry";
import * as Text from "./Text";
import {
	asRegisterable,
	getCollectionEntriesRegistration,
	subscriptionPending,
	getCollectionKeyRegistration,
} from "../state/registerClientState";
import { Destructable } from "../../../../../destructables";
import {
	None,
	Scalar,
	createVariable,
	createDictionary,
	createExpressionBuilder,
	createDictionaryExpressionBuilder,
	WeakCache,
	scalarValue,
} from "../../../../../stream";
import { toStream, toVariable, synchronize } from "../toStream";
import { Pending, Unknown, Failed, Result, isResolved } from "../Status";
import * as expressions from "../state/expressions";
import { default as DestructorTree, addChild } from "../DestructorTree";
import { applyDefaultsWithUpdates } from "./defaults";
import { addLocalEntry } from "../state/addClientState";
import { handleEntryRemoval } from "../state/ClientIdPath";
import { removeAttribute } from "../state/GarbageCollector";
import { iGraph, ordered_graphs } from "../state/Graph";
import { iNode } from "../state/Node";
import { iEntry } from "../state/Entry";
import ClientStateObjectProxy from "./ClientStateObjectProxy";
import { hasCollectionCreationPermission } from "../state 2 interface/permissions";
import { nodeValid, reduceValid, Indirect } from "../state/subscribeToNodePropertyValid";

export const keyEntryMap = WeakCache((collection: api_cs.Ccollection) => {
	const map = createDictionary(
		toStream(collection.properties.entries)
			.map(([, entry]) =>
				toStream(entry.properties.location).stateSwitch({
					remote: (remote) =>
						toStream(remote.properties.marked_for_deletion).stateSwitch({
							yes: () => None as api_cs.Centries__collection | None,
							no: () => entry,
						})(),
					local: () => entry,
				})()
			)
			.filter()
			.groupBy(([, entry]) => {
				return createExpressionBuilder(entry).pipe((entry) => {
					const key_property = entry.properties.key.ref;
					return createExpressionBuilder(Text.synchronized(key_property)).pipe((synchronized) => {
						switch (synchronized) {
							case "pending":
								switch (key_property.properties.backend_known.state.name) {
									case "yes":
										return key_property.properties.backend_known.state.node.properties.value;
									case "no":
										return Pending;
								}
								break;
							case "yes":
								return toStream(key_property.properties.backend_known).stateSwitch({
									yes: (backend_known) => toVariable(backend_known, "value"),
									no: () => Unknown as Result<string>,
								})();
							case "no":
								return toStream(key_property.properties.is_set).stateSwitch({
									yes: (is_set) => toVariable(is_set, "text"),
									no: () => "",
								})();
						}
					})();
				})();
			})()
	);
	const subscription = map.subscribe({ add: () => undefined, remove: () => undefined });
	collection.onDestroy(() => map.unsubscribe(subscription));
	return map;
});

export function getAmountOfLocalEntries(collection: api_cs.Ccollection): number {
	let total = 0;
	for (const entry of collection.properties.entries.values()) {
		const location = entry.properties.location.state;
		switch (location.name) {
			case "remote":
				switch (location.node.properties.marked_for_deletion.state.name) {
					case "no":
						break;
					case "yes":
						total = total - 1;
						break;
				}
				break;
			case "local":
				total = total + 1;
				break;
		}
	}
	return total;
}

const initializedEntriesMap = WeakCache((collection: api_cs.Ccollection) => {
	const stream = createDictionary(
		toStream(collection.properties.entries)
			.map(([, entry]) =>
				toStream(entry.properties.location).stateSwitch({
					remote: () => entry,
					local: () =>
						toStream(entry.properties.node.properties.is_busy_saving).stateSwitch({
							no: () => None as api_cs.Centries__collection | None,
							yes: () => entry,
						})(),
				})()
			)
			.filter()
			.groupBy(([, entry]) =>
				createExpressionBuilder(entry).pipe((entry) =>
					toStream(entry.properties.key.ref.properties.backend_known).stateSwitch({
						yes: (backend_known) => toVariable(backend_known, "value"),
						no: () =>
							toStream(entry.properties.key.ref.properties.is_set).stateSwitch({
								no: () => "",
								yes: ($) => toVariable($, "text"),
							})(),
					})()
				)()
			)()
	);
	const subscription = stream.subscribe({ add: () => undefined, remove: () => undefined });
	collection.onDestroy(() => stream.unsubscribe(subscription));
	return stream;
});

export function getInitializedEntryWithKey(
	collection: api_cs.Ccollection,
	key: string
): api_cs.Centries__collection | undefined {
	const stream = createDictionaryExpressionBuilder(initializedEntriesMap(collection)).get(key)().value;
	if (stream === None) return undefined;
	const [entry] = [...stream.entries.values()];
	return scalarValue(entry);
}

export function findEntryWithKey(collection: api_cs.Ccollection, key: string): api_cs.Centries__collection | undefined {
	for (const entry of collection.properties.entries.values()) {
		if (Entry.getKeyValue(entry) === key) return entry;
	}
	return undefined;
}

export function getEntry(collection: api_cs.Ccollection, key: string): api_cs.Centries__collection | undefined {
	const candidates = new Set<api_cs.Centries__collection>();
	for (const entry of collection.properties.entries.values()) {
		if (Entry.getKeyValue(entry) === key && !Entry.isMarkedForDeletion(entry)) {
			candidates.add(entry);
		}
	}
	if (candidates.size === 1) {
		const [candidate] = candidates;
		return candidate;
	}
	return undefined;
}

export function entriesWithKey(collection: api_cs.Ccollection, key: string) {
	return createDictionaryExpressionBuilder(keyEntryMap(collection)).get(key);
}

export function isInitialized(collection: api_cs.Ccollection): boolean {
	const node = collection.component_root;
	switch (node.properties.location.state.name) {
		case "local":
			return true;
		case "remote": {
			const registration = asRegisterable(getCollectionEntriesRegistration(collection));
			return registration.__subscriptions !== undefined && registration.__subscriptions.count > 0;
		}
		default:
			unexpectedState(node.properties.location.state);
	}
}

export function resolveMatrixSourceCollection(
	collection: api_cs.Ccollection,
	expression: api_app.Centry_reference_definition
) {
	const dummy_node = new api_cs.Centries__collection(
		"dummy",
		{
			location: [
				"local",
				{
					"modified by": ["engine", {}],
				},
			],
			key: "",
			style: { initialized: "no" },
			node: {
				location: [
					"local",
					{
						"parent location": "local",
					},
				],
				"can be deleted": "no",
				"is busy discarding": "no",
				"is busy saving": "no",
				"is valid": "yes",
				"read permitted": "yes",
				attributes: {},
				modified: "no",
				queries: {},
				todo: "no",
				"report types": {},
			},
		},
		collection
	).properties.node;
	const original_properties = dummy_node.properties;
	let defaults_applied = false;
	const attribute_property_name: keyof api_cs.Cnode["properties"] = "attributes";
	const proxy = new Proxy(original_properties, {
		get(target, p) {
			if (!defaults_applied && p === attribute_property_name) {
				defaults_applied = true;
				applyDefaultsWithUpdates(dummy_node, new DestructorTree());
			}
			return (target as any)[p];
		},
	});
	(dummy_node as any).properties = proxy;
	return createExpressionBuilder(dummy_node).pipe(expressions.entry_reference_definition(expression));
}

export function openEntry(collection: api_cs.Ccollection, key: string, destructable: Destructable) {
	// Note: temporary solution while Destructables cannot be used to register objects
	const destructor = new DestructorTree();
	addChild(destructable, destructor);
	return subscriptionPending(getCollectionKeyRegistration(collection, key))
		.pipe((pending) => {
			switch (pending) {
				case true:
					return Pending as Pending | api_cs.Centries__collection | Failed;
				case false:
					return entriesWithKey(collection, key).pipe((entries) => {
						if (entries === None) {
							return Failed;
						} else {
							return createDictionaryExpressionBuilder(entries)
								.get()
								.pipe((entry) => (entry === None ? Failed : entry))();
						}
					})();
			}
		})
		.pipe((entry) => {
			if (!isResolved(entry)) return entry;
			iEntry(entry).register(destructor);
			return entry;
		});
}

type Entry = api_cs.Centries__collection;

class CollectionInterface extends ClientStateObjectProxy {
	graphs: Set<iGraph>;

	readonly name = this.collection.parent.parent.key_value;
	readonly value_source = this.collection.parent.inferences
		.property_type()
		.properties.type.state.node.output.value_source().variant.name;

	get id_path() {
		return createIdPathBuilderFromNode(this.collection.parent.parent.parent);
	}

	constructor(readonly collection: api_cs.Ccollection) {
		super();
		this.graphs = new Set([...ordered_graphs(collection)]);
	}

	find(key: string): Entry | undefined {
		const matches: Entry[] = [];
		for (const entry of this.collection.properties.entries.values()) {
			entry.properties.key.ref.properties.is_set.switch({
				no: () => undefined,
				yes: ($) => {
					if ($.properties.text === key) {
						matches.push(entry);
					}
				},
			});
		}
		if (matches.length !== 1) return undefined;
		return matches[0];
	}

	updateServerKeyValue(entry: Entry, value: string) {
		for (const graph of this.graphs) {
			graph.updateServerKeyValue(entry, value);
		}
	}

	addServerEntry(entry: Entry) {
		for (const graph of this.graphs) {
			graph.addServerEntry(entry);
		}
	}

	__remove_entry(entry: Entry) {
		handleEntryRemoval(entry);
		const last_created_entry = this.collection.properties.last_created_entry.state;
		switch (last_created_entry.name) {
			case "entry":
				if (entry.key_value === last_created_entry.node.properties.entry.entry) {
					this.collection.properties.last_created_entry.set("none", {});
				}
				break;
			case "none":
		}
		entry.root.transact(() => {
			removeAttribute(entry.properties.key.ref.parent.parent);
			entry.parent.properties.entries.delete(entry.key_value);
		});
	}

	removeEntry(entry: Entry) {
		for (const graph of this.graphs) {
			graph.removeEntry(entry);
		}
		this.__remove_entry(entry);
		for (const graph of this.graphs) {
			graph.updateSorting();
		}
	}

	deleteServerEntry(entry: Entry) {
		for (const graph of this.graphs) {
			graph.deleteServerEntry(entry);
		}
		this.__remove_entry(entry);
		for (const graph of this.graphs) {
			graph.updateSorting();
		}
	}

	__addEntry(by?: "user" | "engine", key?: string) {
		return addLocalEntry(this.collection, by, key);
	}

	addEntry(by?: "user" | "engine", key?: string) {
		const entry = this.__addEntry(by, key);
		for (const graph of this.graphs) {
			graph.addEntry(entry, by);
		}
		return entry;
	}

	deleteEntry(entry: Entry) {
		const location = entry.properties.location.state;
		switch (location.name) {
			case "remote":
				location.node.properties.marked_for_deletion.ensure("yes", {});
				for (const graph of this.graphs) {
					graph.deleteEntry(entry);
				}
				break;
			case "local":
				for (const graph of this.graphs) {
					graph.deleteEntry(entry);
				}
				this.__remove_entry(entry);
				break;
		}
	}

	discard() {
		for (const entry of this.collection.properties.entries.values()) {
			switch (entry.properties.location.state.name) {
				case "remote":
					entry.properties.location.state.node.properties.marked_for_deletion.ensure("no", {});
					iNode(entry.properties.node).discard();
					break;
				case "local":
					this.removeEntry(entry);
					break;
			}
		}
		for (const graph of this.graphs) {
			graph.reset();
		}
	}

	graphNodeIsModified(entry: Entry) {
		return [...this.graphs].reduce((modified, graph) => modified || graph.getNode(entry)?.isModified(), false);
	}

	clearDefaultEntries() {
		for (const entry of this.collection.properties.entries.values()) {
			switch (entry.properties.location.state.name) {
				case "remote":
					break;
				case "local": {
					const local_entry = entry.properties.location.state.node;
					switch (local_entry.properties.modified_by.state.name) {
						case "user":
							break;
						case "engine":
							for (const graph of this.graphs) {
								graph.deleteEntry(entry);
							}
							this.__remove_entry(entry);
					}
				}
			}
		}
	}

	readonly amountOflocalEntries = createVariable(
		toStream(this.collection.properties.entries)
			.map(([, entry]) =>
				toStream(entry.properties.location).stateSwitch({
					remote: (remote) =>
						toStream(remote.properties.marked_for_deletion).stateSwitch({
							yes: () => -1,
							no: () => 0,
						})(),
					local: () => 1,
				})()
			)
			.reduce((total, value) => total + value, 0)(),
		false
	);

	private createAmountOfRemoteEntriesQuery() {
		return DatastoreProxy.instance
			.collectionQuery({
				filters: {
					"todo filter": ["no", {}],
					"value filters": {},
				},
				"context node path": this.id_path.build(),
				"collection path": CollectionPathBuilder.create().collection(this.name).build(),
				"select entries": ["no", {}],
				aggregates: {},
			})
			.then((result) => {
				switch (result.properties.context.state.name) {
					case "exists":
						return result.properties.context.state.node.properties.amount_of_entries.value;
					case "does not exist":
					case "permission denied":
				}
				return Failed;
			});
	}
	private amountOfRemoteEntriesUpdater = createVariable<() => Promise<Pending | number | Failed>>(
		() => this.createAmountOfRemoteEntriesQuery(),
		true
	);
	updateAmountOfRemoteEntries() {
		this.amountOfRemoteEntriesUpdater.set(() => this.createAmountOfRemoteEntriesQuery());
	}

	readonly amountOfRemoteEntries = createVariable(
		toStream(this.collection.component_root.properties.location).stateSwitch({
			local: () => 0,
			remote: () =>
				createExpressionBuilder(
					synchronize(createExpressionBuilder(this.amountOfRemoteEntriesUpdater).pipe((update) => update())())
				).pipe((value) => (typeof value === "number" ? value : 0))(),
		})(),
		false
	);

	readonly amountOfEntries = createVariable(
		createExpressionBuilder(this.amountOflocalEntries)
			.and(this.amountOfRemoteEntries)
			.pipe(([local, remote]) => {
				const amount_of_entries = local + remote;
				this.collection.set_amount_of_entries(amount_of_entries);
				return amount_of_entries;
			})(),
		false
	);

	readonly isEmpty = createVariable(
		createExpressionBuilder(this.amountOfEntries).pipe((amount_of_entries) => {
			switch (amount_of_entries) {
				case 0:
					this.collection.properties.is_empty.ensure("yes", {});
					break;
				case 1:
					this.collection.properties.is_empty
						.ensure("no", {
							size: ["single", {}],
						})
						.properties.size.ensure("single", {});
					break;
				default:
					this.collection.properties.is_empty
						.ensure("no", {
							size: ["multiple", {}],
						})
						.properties.size.ensure("multiple", {});
					break;
			}
			return this.collection.properties.is_empty;
		})(),
		false
	);

	private mutationPermissionUpdater = createVariable<() => Promise<Pending | boolean>>(
		() => new Promise((resolve) => hasCollectionCreationPermission(this.id_path, this.name, resolve)),
		true
	);
	updateMutationPermissions() {
		this.mutationPermissionUpdater.set(
			() => new Promise((resolve) => hasCollectionCreationPermission(this.id_path, this.name, resolve))
		);
	}

	readonly canCreateEntry = createVariable(
		toStream(this.collection.component_root.properties.location)
			.stateSwitch({
				local: () => this.value_source === "base",
				remote: () =>
					createExpressionBuilder(
						synchronize(createExpressionBuilder(this.mutationPermissionUpdater).pipe((update) => update())())
					).pipe((value) => value === true)(),
			})
			.pipe((create_permitted) => {
				if (create_permitted) {
					this.collection.properties.is_editable.ensure("yes", {
						"edit mode": ["off", {}],
					});
					this.collection.properties.can_create_entry.ensure("yes", {});
				} else {
					this.collection.properties.can_create_entry.ensure("no", {});
				}
				return create_permitted;
			})(),
		false
	);

	readonly modified = createVariable(
		toStream(this.collection.properties.entries)
			.map(([, entry]) =>
				toStream(entry.properties.location).stateSwitch({
					local: ($) =>
						toStream($.properties.modified_by).stateSwitch({
							user: () => "user" as const,
							engine: () => "engine" as const,
						})(),
					remote: ($) =>
						toStream($.properties.marked_for_deletion).stateSwitch({
							no: () => "no" as const,
							yes: () => "user" as const,
						})(),
				})()
			)
			.reduce((acc: "no" | "user" | "engine", $) => {
				if ($ === "user") return "user";
				return acc === "no" ? $ : acc;
			}, "no")
			.pipe((modified) => {
				if (modified === "no") {
					this.collection.properties.modified.ensure("no", {});
				} else {
					this.collection.properties.modified
						.ensure("yes", {
							by: modified,
						})
						.properties.by.ensure(modified, {});
				}
				return modified === "no" ? false : true;
			})(),
		false
	);

	readonly entriesAreValid: Scalar<boolean | Indirect> = createVariable(
		toStream(this.collection.properties.entries)
			.map(([, entry]) =>
				toStream(entry.properties.location).stateSwitch({
					local: () => nodeValid(entry.properties.node),
					remote: ($) =>
						toStream($.properties.marked_for_deletion).stateSwitch({
							no: () => nodeValid(entry.properties.node),
							yes: () => true,
						})(),
				})()
			)
			.reduce(reduceValid, true)(),
		false
	);

	readonly cardinalityConstraintViolated = createVariable(
		this.collection.inferences.collection_type().properties.cardinality_constraint.properties.constraint.switch({
			"non empty": () =>
				toStream(this.collection.component_root.properties.location)
					.stateSwitch({
						local: () => true,
						remote: () => this.modified,
					})
					.pipe((modified_or_local) =>
						modified_or_local
							? createExpressionBuilder(this.amountOfEntries).pipe((amount_of_entries) => amount_of_entries <= 0)()
							: false
					)(),
			none: () => false as Scalar<boolean>,
		}),
		false
	);

	isValid = createVariable(
		createExpressionBuilder(this.entriesAreValid)
			.and(this.cardinalityConstraintViolated)
			.pipe(([entries_are_valid, cardinality_constraint_violation]) => {
				const direct_invalid = cardinality_constraint_violation || entries_are_valid === false;
				const indirect_invalid = entries_are_valid === Indirect;
				if (direct_invalid || indirect_invalid) {
					const invalid = this.collection.properties.is_valid.ensure("no", {
						"entries are invalid": ["no", {}],
						"cardinality constraint violation": ["no", {}],
					});
					invalid.properties.entries_are_invalid.ensure(entries_are_valid ? "no" : "yes", {});
					invalid.properties.cardinality_constraint_violation.ensure(
						cardinality_constraint_violation ? "yes" : "no",
						{}
					);
					if (!direct_invalid) {
						return Indirect;
					}
					return false;
				} else {
					this.collection.properties.is_valid.ensure("yes", {});
					return true;
				}
			})(),
		false
	);
}

export type iCollection = CollectionInterface;

export const iCollection = WeakCache<api_cs.Ccollection, CollectionInterface>(
	(collection) => new CollectionInterface(collection)
);
